import { useTranslation } from 'react-i18next'
import { LanguageIcon } from '@heroicons/react/24/outline'
import { useLanguage } from '../hooks/useLanguage'
import type { SupportedLanguage } from '../i18n/config'

interface LanguageSelectorProps {
  /** 설정 화면처럼 제목·설명이 함께 필요한 경우 */
  withLabel?: boolean
}

/** 화면 언어 선택 */
export default function LanguageSelector({
  withLabel = false,
}: LanguageSelectorProps) {
  const { t } = useTranslation()
  const { language, supported, changeLanguage } = useLanguage()

  const control = (
    <div
      role="radiogroup"
      aria-label={t('language.title')}
      className="inline-flex rounded-xl border border-gray-300 bg-white p-1 dark:border-gray-600 dark:bg-gray-800"
    >
      {supported.map(option => (
        <button
          key={option.code}
          type="button"
          role="radio"
          aria-checked={language === option.code}
          // 화면에는 Ko/En 으로 짧게, 읽어주는 이름은 언어 전체 이름으로
          aria-label={t(`language.${option.code}`)}
          onClick={() => changeLanguage(option.code as SupportedLanguage)}
          className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
            language === option.code
              ? 'bg-primary-600 text-white'
              : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  )

  if (!withLabel) {
    return control
  }

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
            <LanguageIcon className="h-5 w-5 text-gray-400" />
            {t('language.title')}
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {t('language.description')}
          </p>
        </div>
        {control}
      </div>
    </section>
  )
}
